/**
 * Visitor-origin capture — first-touch + last-touch, in localStorage.
 *
 * No cookie, no third party, no network. This is read once at quote submit to
 * fill the Odoo crm.lead (source/medium/campaign + gclid/fbclid/referrer). The
 * backend re-sanitizes everything at the route boundary; this module only keeps
 * the signal alive across the landing → configurator navigation (same origin,
 * so localStorage traverses). SSR-safe: every function no-ops without `window`.
 *
 * Consumed by landingpage/gatsby-browser.js and configurator/gatsby-browser.js
 * (onClientEntry + onRouteUpdate) and by the quote-submit POST bodies.
 */

const KEY = 'objects_attribution';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
const CLICK_KEYS = ['gclid', 'fbclid', 'msclkid'];

function readStore() {
  try {
    return JSON.parse(window.localStorage.getItem(KEY)) || {};
  } catch (_) {
    return {};
  }
}

function writeStore(value) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(value));
  } catch (_) {
    // Private mode / storage disabled — degrade to no-op, never throw.
  }
}

// Origin markers on the current URL + external referrer. Returns {} for a
// direct or internal navigation that carries no campaign markers.
function readSignal() {
  const params = new URLSearchParams(window.location.search);
  const touch = {};
  UTM_KEYS.concat(CLICK_KEYS).forEach((k) => {
    const v = params.get(k);
    if (v) touch[k] = String(v).slice(0, 200);
  });
  // Referrer only when external — an internal SPA hop must not overwrite the
  // real first referrer with our own host.
  const ref = typeof document !== 'undefined' ? document.referrer || '' : '';
  if (ref && !ref.includes(window.location.host)) touch.referrer = ref.slice(0, 200);
  return touch;
}

function hasOrigin(touch) {
  return UTM_KEYS.concat(CLICK_KEYS).some((k) => touch[k]) || Boolean(touch.referrer);
}

/**
 * Record the current visit. First-touch is written once (even a direct visit,
 * so we still keep the landing page). Last-touch is overwritten only when THIS
 * visit carries a real origin signal, so internal navigation never erases the
 * last campaign. Idempotent — safe to call from multiple browser hooks.
 */
export function captureAttribution() {
  if (typeof window === 'undefined') return;
  const signal = readSignal();
  const landing = (window.location.pathname + window.location.search).slice(0, 200);
  const store = readStore();
  if (!store.first) {
    store.first = { ...signal, landing_page: landing, ts: Date.now() };
  }
  if (hasOrigin(signal)) {
    store.last = { ...signal, landing_page: landing, ts: Date.now() };
  }
  writeStore(store);
}

/**
 * The attribution payload to ride along with a quote POST. Returns undefined
 * when nothing was ever captured, so the body key stays absent and the backend
 * runs its legacy path.
 */
export function getAttribution() {
  if (typeof window === 'undefined') return undefined;
  const store = readStore();
  if (!store.first && !store.last) return undefined;
  return {
    ...(store.first ? { first: store.first } : {}),
    ...(store.last ? { last: store.last } : {}),
  };
}
